import { __rest } from "tslib";
import hoistStatics from 'hoist-non-react-statics';
import { createClient } from 'mage-core';
import * as R from 'ramda';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createAction as reduxCreateAction } from 'redux-actions';
import { matchCapabilitiesToProps } from '../utils';
import CapabilitiesContext from './CapabilitiesContext';
const replaceKeys = (s, [k, v]) => R.replace(`{{${k}}}`, v, s);
const createUrl = (uri = '', params = {}) => R.reduce(replaceKeys, uri, R.toPairs(params));
export default function withCapabilities(mapCapabilitiesToProps, { meta = {} } = {}) {
    const capabilitiesFactoryCreator = matchCapabilitiesToProps(mapCapabilitiesToProps);
    return function withCapabilitiesInner(WrappedComponent) {
        const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
        const wrappedComponentName = `WithCapabilities(${displayName})`;
        const capabilitiesFactory = capabilitiesFactoryCreator({ wrappedComponentName });
        const WithCapabilities = (props) => {
            const context = React.useContext(CapabilitiesContext);
            const client = useSelector(createClient);
            const dispatch = useDispatch();
            const createRequest = (cap, contract) => {
                const pathParams = R.compose(R.pluck('name'), R.filter((p) => p.type === 'path'))(contract.params);
                return (overrides = {}) => {
                    const _a = R.mergeDeepRight(cap.options, overrides), { body, params = {} } = _a, opts = __rest(_a, ["body", "params"]);
                    const path = R.pick(pathParams, params);
                    const query = R.omit(pathParams, params);
                    const url = createUrl(contract.uri, path);
                    const request = {
                        method: contract.method,
                        headers: contract.headers,
                        url: url,
                        params: query,
                        data: body
                    };
                    return client.request(R.mergeDeepRight(request, opts));
                };
            };
            const createAction = (cap, contract) => {
                const loading = reduxCreateAction(cap.LOADING, undefined, () => meta);
                const perform = reduxCreateAction(cap.CAPABILITY, createRequest(cap, contract), () => meta);
                return overrides => [
                    loading(),
                    perform(overrides)
                ];
            };
            const getCapability = (cap) => {
                const contract = context[cap.KEY];
                return cap.valid && contract && createAction(cap, contract);
            };
            const addExtraProps = (caps) => {
                return bindActionCreators(R.compose(R.filter(R.identity), R.map(getCapability))(caps), dispatch);
            };
            return React.createElement(WrappedComponent, R.mergeDeepRight(Object.assign(Object.assign({}, props), { client, dispatch }), addExtraProps(capabilitiesFactory(props))));
        };
        WithCapabilities.displayName = wrappedComponentName;
        WithCapabilities.WrappedComponent = WrappedComponent;
        return hoistStatics(WithCapabilities, WrappedComponent);
    };
}
